import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, BookOpen, Users, Tags, BarChart3, LogOut, ShieldCheck, Settings, Building2, PlusCircle, Briefcase, History, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Footer } from "@/components/Footer";

const ALL_ROLES = ["admin", "manager", "editor", "viewer"];
const ADMIN_ROLES = ["admin", "manager"];

const baseNavItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ALL_ROLES },
  { to: "/ledger", label: "Ledger", icon: BookOpen, testid: "nav-ledger", roles: ALL_ROLES },
  { to: "/parties", label: "Parties", icon: Users, testid: "nav-parties", roles: ALL_ROLES },
  { to: "/categories", label: "Categories", icon: Tags, testid: "nav-categories", roles: ALL_ROLES },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", roles: ALL_ROLES },
  { to: "/users", label: "Users", icon: ShieldCheck, testid: "nav-users", roles: ADMIN_ROLES },
  { to: "/audit-log", label: "Audit Log", icon: History, testid: "nav-audit-log", roles: ADMIN_ROLES },
  { to: "/business", label: "Business", icon: Settings, testid: "nav-business", roles: ADMIN_ROLES },
];

const roleColor = {
  admin: "bg-[#0F172A] text-white",
  manager: "bg-[#7C3AED] text-white",
  editor: "bg-[#10B981] text-white",
  viewer: "bg-[#6B7280] text-white",
};

export default function Layout() {
  const { user, logout, isAppOwner } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    let mounted = true;
    api.get("/business/me")
      .then((r) => { if (mounted) setBiz(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [user?.business_id]);

  // Prevent body scroll while mobile sidebar is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (sidebarOpen) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = baseNavItems.filter((it) => it.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-white" data-testid="app-layout">
      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed off-canvas on mobile, static on lg+ */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 border-r border-[#E5E7EB] flex flex-col bg-[#F9FAFB] transform transition-transform duration-200 ease-out lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
        aria-hidden={!sidebarOpen ? "true" : undefined}
      >
        <div className="px-4 pt-5 pb-3 text-center border-b border-[#E5E7EB] bg-white flex items-center justify-between gap-2" data-testid="app-brand">
          <div className="font-display text-2xl font-extrabold tracking-tight text-[#0F172A] leading-none flex-1">
            SWIFT<span className="text-[#0080FF]"> REGISTER</span>
          </div>
          {/* Close button — only on mobile */}
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close"
            className="lg:hidden p-1 text-[#6B7280] hover:text-[#0F172A]"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-[#E5E7EB] bg-white flex items-center justify-center overflow-hidden shrink-0" data-testid="biz-logo">
              {biz?.logo_data_uri ? (
                <img src={biz.logo_data_uri} alt={biz.name} className="w-full h-full object-contain" />
              ) : (
                <Building2 className="h-5 w-5 text-[#9CA3AF]" />
              )}
            </div>
            <div className="min-w-0">
              <div className="overline truncate">Workspace</div>
              <div className="text-sm font-semibold text-[#0F172A] truncate" data-testid="biz-name-display">{biz?.name || "—"}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {navItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-all duration-150 ${
                    isActive
                      ? "border-[#333333] bg-[#333333] text-white"
                      : "border-transparent text-[#6B7280] hover:text-[#0F172A] hover:bg-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {it.label}
              </NavLink>
            );
          })}
          {isAppOwner && (
            <div className="pt-4 mt-4 border-t border-[#E5E7EB]">
              <div className="overline px-3 mb-2">Platform</div>
              <NavLink
                to="/admin/businesses"
                data-testid="nav-admin-businesses"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-all duration-150 ${
                    isActive
                      ? "border-[#333333] bg-[#333333] text-white"
                      : "border-transparent text-[#6B7280] hover:text-[#0F172A] hover:bg-white"
                  }`
                }
                title="Manage all business workspaces on the platform"
              >
                <Briefcase className="h-4 w-4" />
                All Businesses
              </NavLink>
              <NavLink
                to="/signup"
                data-testid="nav-create-workspace"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-all duration-150 ${
                    isActive
                      ? "border-[#333333] bg-[#333333] text-white"
                      : "border-transparent text-[#10B981] hover:text-[#0F172A] hover:bg-white"
                  }`
                }
                title="Create a new business workspace"
              >
                <PlusCircle className="h-4 w-4" />
                Create Workspace
              </NavLink>
              <div className="px-3 mt-1 text-[10px] text-[#9CA3AF] leading-snug">
                Existing workspace? Ask your admin to add you.
              </div>
            </div>
          )}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto flex flex-col min-w-0" data-testid="main-content">
        {/* Top user bar — hamburger on mobile, user info + logout always */}
        <div
          className="flex items-center justify-between gap-3 px-4 lg:px-6 py-2 bg-white border-b border-[#E5E7EB] text-xs"
          data-testid="top-user-bar"
        >
          {/* Left — hamburger (mobile only) */}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-open"
            className="lg:hidden p-1.5 text-[#0F172A] hover:bg-[#F3F4F6]"
            aria-label="Open sidebar"
            aria-expanded={sidebarOpen}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          {/* Right — user identity + logout */}
          <div className="flex items-center gap-2 lg:gap-3 min-w-0">
            <span className="hidden sm:inline text-[#6B7280] uppercase tracking-wider">Signed in as</span>
            <span className="font-semibold text-[#111827] truncate" data-testid="current-user-name">{user?.name}</span>
            <span className="hidden md:inline text-[#6B7280]">·</span>
            <span className="hidden md:inline text-[#4B5563] truncate">{user?.email}</span>
            {user?.role && (
              <span
                className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${roleColor[user.role] || roleColor.viewer}`}
                data-testid="user-role-badge"
              >
                {user.role}
              </span>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="ml-1 lg:ml-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 lg:px-3 py-1.5 border border-[#E5E7EB] hover:border-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-all duration-150"
            >
              <LogOut className="h-3 w-3" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <div className="flex-1">
          <Outlet />
        </div>
        <Footer />
      </main>
    </div>
  );
}
